import React, { useContext, useRef } from "react";
import Icon from "@mdi/react";
import { mdiLock } from "@mdi/js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { getGameLeague, getGameProjections, isGameProjectionUnavailable, useGame } from "src/services/EventService";
import { SubscriptionContext } from "src/services/SubscriptionService";
import styles from "./styles.scss";
import Spinner from "src/components/Spinner";
import TeamLogo from "../Team/TeamLogo";
import { useContainerQuery } from "src/services/UtilService";

const getProjectionColors = (p: any) => {
  const isNumber = Number.isFinite(parseFloat(p.home));
  const isBoolean = [true, false].includes(p.home);
  const isLetterGrade = !isNumber && typeof p.home === "string" && Boolean(p.home.match(/[ABCD]/)); // prettier-ignore

  const colors = { red: "#ff4f4d", yellow: "#fba836", green: "#02a16c" };

  let homeColor = "inherit";
  let awayColor = "inherit";

  if (p.noColor) {
    return { home: homeColor, away: awayColor };
  }

  if (isNumber) {
    if (parseFloat(p.home) > parseFloat(p.away)) {
      homeColor = colors.green;
      awayColor = colors.red;
    }

    if (parseFloat(p.away) > parseFloat(p.home)) {
      awayColor = colors.green;
      homeColor = colors.red;
    }
  }

  if (isBoolean) {
    homeColor = p.home ? colors.green : colors.red;
    awayColor = p.away ? colors.green : colors.red;
  }

  const letterColors: any = {
    A: colors.green,
    B: colors.green,
    C: colors.yellow,
    D: colors.red,
  };

  if (isLetterGrade) {
    homeColor = letterColors[p.home.match(/[ABC]/)?.[0] ?? "D"];
    awayColor = letterColors[p.away.match(/[ABC]/)?.[0] ?? "D"];
  }

  return { home: homeColor, away: awayColor };
};

const ProjectedValue = ({ value, style }: { value: any; style: any }) => {
  const isBoolean = [true, false].includes(value);

  if (isBoolean) {
    return <FontAwesomeIcon className="projected-value" style={style} icon={value ? faCheck : faTimes} />;
  }

  const valueClass = value === "--" ? "projected-value projected-value--placeholder" : "projected-value";

  return (
    <span className={valueClass} style={style}>
      {value}
    </span>
  );
};

const GameProjection = ({
  projection,
  game,
  projectionUnavailable,
}: {
  projection: any;
  game: any;
  projectionUnavailable: boolean;
}) => {
  const ctx = useContext(SubscriptionContext);
  const league = getGameLeague(game);
  const hasAccess = ctx.hasSubscriptionAccess(league.key) || game.freeGame;
  const colors = getProjectionColors(projection);

  const lock = (
    <div
      className="lock"
      onClick={() => {
        ctx.togglePricingModal({
          track: {
            page: window.location.href,
            component: "GameProjection",
            sport: league.name,
            source: "GameProjection",
          },
          source: "GameProjection",
        });
      }}
    >
      <Icon path={mdiLock} size="17px" color="#004198" />
    </div>
  );

  return (
    <div className="game-projection">
      <span>{!hasAccess ? lock : <ProjectedValue value={projection.away} style={{ color: colors.away }} />}</span>
      <span className={projectionUnavailable ? "projection-label projection-label--muted" : "projection-label"}>
        {projection.name}
      </span>
      <span>{!hasAccess ? lock : <ProjectedValue value={projection.home} style={{ color: colors.home }} />}</span>
    </div>
  );
};

const GameProjections = ({ slug, include }: { slug: string; include?: string[] }) => {
  const ctx = useContext(SubscriptionContext);
  const { game } = useGame({ slug });

  const ref = useRef(null);
  useContainerQuery(ref, {
    small: 600,
    large: Infinity,
  });

  if (!game) {
    return (
      <div className="game-projections loading" ref={ref}>
        <Spinner size={32} color="#ebebeb" />
        <style jsx>{styles}</style>
      </div>
    );
  }

  const league = getGameLeague(game);
  const hasAccess = ctx.hasSubscriptionAccess(league.key) || game.freeGame;
  const projectionUnavailable = isGameProjectionUnavailable(game);

  let projections = getGameProjections(game);

  if (include) {
    projections = projections.filter((p) => include.includes(p.name));
  }

  const getTeamLogo = (team: any) => (
    <TeamLogo
      size={32}
      leagueName={league.imagePath}
      teamAbbreviation={team[league.sport.name === "Tennis" ? "country" : "preferredAbbreviation"]} // prettier-ignore
      containerClassName="team-logo"
    />
  );

  return (
    <div className={`game-projections${projectionUnavailable ? " game-projections--unavailable" : ""}`} ref={ref}>
      <div className="game-projections-header">
        <div className="team-heading">
          {getTeamLogo(game.awayTeam)}
          <span>{game.awayTeam.lastName}</span>
        </div>
        <div className="team-heading">
          <span>{game.homeTeam.lastName}</span>
          {getTeamLogo(game.homeTeam)}
        </div>
      </div>
      <div className="game-projections-list">
        {projections.map((projection, idx) => (
          <GameProjection key={idx} projection={projection} game={game} projectionUnavailable={projectionUnavailable} />
        ))}
      </div>
      {!hasAccess && (
        <div className="game-projections-cta">
          Subscribe to Access All {league.name} Predictions Including {game.awayTeam.lastName} vs.{" "}
          {game.homeTeam.lastName}{" "}
        </div>
      )}
      <style jsx>{styles}</style>
    </div>
  );
};

export const GameProjectionsCard = ({ slug, include }: { slug: string; include?: string[] }) => {
  return (
    <div className="game-projections-card">
      <GameProjections slug={slug} include={include} />
      <style jsx>{styles}</style>
    </div>
  );
};

export default GameProjections;
